import React, { useState } from "react";
import { Button } from "../components/Button.jsx";
import { useGame, usePlayer } from "@empirica/core/player/classic/react";
import { getTaskConfig } from "../utils/taskConfig.js";
import { savePlayerMeta } from "../utils/savePlayerMeta.js";
import {
  getCompensationConfig,
  getPostConfirmPolishMode,
  getPreLobbyMode,
  getReflectionTiming,
  isBaseRateGroup3Condition,
  isBaseRateStaticSyntheticGroupCondition,
  suppressesPostConfirmWriteup,
  usesPostInitialConditionInstructions,
} from "../utils/conditionUtils.js";

export function ControlInstructions({ next }) {
  const game = useGame();
  const player = usePlayer();
  const treatment = game?.get("treatment") || {};
  const taskConfig = getTaskConfig(treatment);
  const preLobbyMode = getPreLobbyMode(treatment);
  const hasPostConfirmPolishStep = Boolean(getPostConfirmPolishMode(treatment));
  const isBaseRateGroup3 = isBaseRateGroup3Condition(treatment);
  const isBaseRateStaticSyntheticGroup =
    isBaseRateStaticSyntheticGroupCondition(treatment);
  const usesShareableBaseRateGroup =
    isBaseRateGroup3 || isBaseRateStaticSyntheticGroup;
  const usesTemplateFollowUp =
    usesShareableBaseRateGroup && preLobbyMode === "template";
  // The baseline arm runs no reflection activity between the first estimate and
  // the peer review, so its step list, its step numbering, and its first
  // comprehension question all have to describe a three-step session rather
  // than naming a follow-up questionnaire or an AI assistant it never shows.
  const usesNoFollowUp = usesShareableBaseRateGroup && preLobbyMode === "none";
  // Arms that hold the condition-specific instructions until after the initial
  // estimate. This page is then the same page in all three of them: it names no
  // preparation activity, numbers the steps the same way, and asks the same
  // comprehension questions, so nothing a participant sees before the initial
  // estimate and confidence rating depends on the assigned condition.
  const usesNeutralPreEstimateSteps =
    usesPostInitialConditionInstructions(treatment);
  // The neutral spine is the three-step list the baseline arm already shows:
  // own estimate, peer review, final estimate. The template and Socratic arms
  // borrow it wholesale, so the two cases share every branch below.
  const usesThreeStepList = usesNeutralPreEstimateSteps || usesNoFollowUp;
  // Arms that set initialRationaleMode never render the post-confirm write-up,
  // so the step list must not promise one and the later steps move up a number.
  const skipsPostConfirmWriteup = suppressesPostConfirmWriteup(treatment);
  const groupSize = treatment?.playerCount || treatment?.factors?.playerCount || 3;
  const displayGroupSize = usesShareableBaseRateGroup ? 3 : groupSize;
  const compensation = getCompensationConfig(treatment);
  // Post-peer conditions run the peer review before the reflection activity, so
  // the numbered process steps below follow a different order.
  //
  // The post-peer follow-up arms also hold the condition-specific instructions
  // until after the initial estimate. For them this page shows the neutral
  // three-step list the main study's arms show, which names no activity and no
  // answer format, so everything before the initial estimate is the same page
  // in the template and AI arms. The post-peer step lists below therefore apply
  // only to post-peer arms with the default pre-initial instructions.
  const isPostPeerTiming =
    isBaseRateStaticSyntheticGroup &&
    getReflectionTiming(treatment) === "post-peer" &&
    !usesNeutralPreEstimateSteps;
  // The askall pair has no second estimate after the peer review, discusses all
  // three answers in the reflection activity, and restricts the final answer to
  // a choice among those answers, so its step list differs from the other
  // post-peer conditions.
  const postPeerAskAll =
    treatment?.postPeerAskAll === true ||
    treatment?.factors?.postPeerAskAll === true;
  const discussionStepNumber = isPostPeerTiming
    ? 2
    : usesThreeStepList
      ? 2
      : usesShareableBaseRateGroup
        ? skipsPostConfirmWriteup
          ? 4
          : hasPostConfirmPolishStep
            ? 6
            : 5
        : 2;
  const finalVoteStepNumber = isPostPeerTiming
    ? postPeerAskAll
      ? 4
      : 5
    : usesThreeStepList
      ? 3
      : usesShareableBaseRateGroup
        ? skipsPostConfirmWriteup
          ? 5
          : hasPostConfirmPolishStep
            ? 7
            : 6
        : 3;
  const [answers, setAnswers] = useState({ q1: "", q2: "", q3: "", q4: "" });
  const [showError, setShowError] = useState(false);

  const correctAnswers = usesThreeStepList
    ? {
        // q1 asks what happens before the final estimate, not what happens on
        // the very next screen. The next screen differs across the three arms
        // and the peer review does not, so this is the form of the question
        // that is true in all of them.
        q1: "review",
        q2: "individual",
        q3: "not-simultaneous",
      }
    : isBaseRateStaticSyntheticGroup
    ? {
        q1: "detailed",
        q2: "individual",
        q3: "not-simultaneous",
      }
    : usesShareableBaseRateGroup
    ? {
        q1: "detailed",
        q2: "group",
        q3: "convincing",
        q4: "no-majority",
      }
    : {
        q1: "detailed",
        q2: "group",
        q3: "convincing",
        q4: "edit",
      };

  const handleAnswerChange = (question, value) => {
    setAnswers({ ...answers, [question]: value });
    setShowError(false);
  };

  const handleNext = () => {
    const allCorrect = Object.keys(correctAnswers).every(
      key => answers[key] === correctAnswers[key]
    );

    // The check is a gate: participants cannot advance until every answer is
    // correct, so the passing answers carry no information. What varies across
    // participants is how many attempts it takes and which questions they miss,
    // and the preregistration lists comprehension-check performance as a
    // measured variable. Record it here, on every submission, so the measure
    // exists in the export.
    const attemptNumber = (player.get("instructionCheckAttempts") || 0) + 1;
    const missedQuestions = Object.keys(correctAnswers).filter(
      key => answers[key] !== correctAnswers[key]
    );
    const attemptLog = player.get("instructionCheckLog") || [];
    const updatedLog = [
      ...attemptLog,
      {
        attempt: attemptNumber,
        answers: { ...answers },
        missedQuestions,
        passed: allCorrect,
        timestamp: Date.now(),
      },
    ];
    player.set("instructionCheckAttempts", attemptNumber);
    player.set("instructionCheckLog", updatedLog);
    if (allCorrect) {
      player.set("instructionCheckPassedFirstTry", attemptNumber === 1);
      savePlayerMeta({
        player,
        name: "instructionCheckLog",
        value: updatedLog,
        debugTag: "instructions: comprehension check",
      });
    }

    if (allCorrect) {
      next();
    } else {
      setShowError(true);
    }
  };

  return (
    <div className="mt-3 sm:mt-5 p-20">
      <h3 className="text-lg leading-6 font-medium text-gray-900">
        Instructions
      </h3>
      <div className="mt-3 mb-6 space-y-6 text-gray-800">
        {/* 1. Objective of the game */}
        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">1. Objective of the Game</h4>
          <p>
            {isBaseRateStaticSyntheticGroup ? (
              taskConfig.studyObjectiveText
            ) : usesShareableBaseRateGroup ? (
              <>
                You will be playing this game in a <strong>{displayGroupSize}-person group</strong>. The objective is to make an accurate prediction, which will be determined by taking the <strong>majority vote</strong> of your group.
              </>
            ) : (
              <>
                You will be playing this game with other participants. The objective is to make an accurate prediction, which will be determined by taking the <strong>majority vote</strong> of your group.
              </>
            )}
          </p>
          {isBaseRateStaticSyntheticGroup && taskConfig.groupMindsetPrompt && (
            <p className="mt-2">{taskConfig.groupMindsetPrompt}</p>
          )}
          {taskConfig.introPrompt && !isBaseRateStaticSyntheticGroup && (
            <p className="mt-2">{taskConfig.introPrompt}</p>
          )}
        </div>

        {/* 2. Process of the game */}
        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">2. Process of the Game</h4>
          <div className="space-y-2">
            {taskConfig.quizEnabled && (
              <p>
                <strong>Step 0 (Quiz):</strong> You will first complete a <strong>10-question quiz</strong> based on loan cases. This quiz is used to evaluate baseline knowledge in the domain. You will not see the correct answers during the quiz.
              </p>
            )}
            {isPostPeerTiming && postPeerAskAll ? (
              <>
                <p>
                  <strong>Step 1:</strong> You will make your <strong>own individual prediction</strong>. This is the only step where you type in a number of your own: your final answer at the end will be <strong>a choice among three options</strong> (see Step 4).
                </p>
                <p>
                  <strong>Step 2:</strong> You will review <strong>two other participants' responses</strong> to the same question. There is <strong>no live back-and-forth discussion</strong>. Use their estimates and reasoning as you would peer input in a group decision. When you are ready, click Continue to move on.
                </p>
                <p>
                  <strong>Step 3:</strong>{" "}
                  {usesTemplateFollowUp ? (
                    <>
                      You will then complete a <strong>structured follow-up questionnaire</strong> about <strong>each of the three answers</strong>: your own and the two other participants'. Please provide <strong>a detailed explanation</strong> of your reasoning.
                    </>
                  ) : (
                    <>
                      You will then <strong>converse with an AI assistant</strong> about <strong>each of the three answers</strong>, one at a time: your own and the two other participants'. Please provide <strong>a detailed explanation</strong> of your reasoning.
                    </>
                  )}
                </p>
              </>
            ) : isPostPeerTiming ? (
              <>
                <p>
                  <strong>Step 1:</strong> You will make your <strong>own individual prediction</strong>.
                </p>
                <p>
                  <strong>Step 2:</strong> You will review <strong>two other participants' responses</strong> to the same question. There is <strong>no live back-and-forth discussion</strong>. Use their estimates and reasoning as you would peer input in a group decision. When you are ready, click Continue to move on.
                </p>
                <p>
                  <strong>Step 3:</strong> You will <strong>give your estimate again</strong>, now that you have seen their responses. You may keep it the same or change it.
                </p>
                <p>
                  <strong>Step 4:</strong>{" "}
                  {usesTemplateFollowUp ? (
                    <>
                      You will then complete a <strong>structured follow-up questionnaire</strong>. Please provide <strong>a detailed explanation</strong> of your reasoning.
                    </>
                  ) : (
                    <>
                      You will then <strong>converse with an AI assistant</strong> about your estimate. Please provide <strong>a detailed explanation</strong> of your reasoning.
                    </>
                  )}
                </p>
              </>
            ) : usesThreeStepList ? (
              <>
                <p>
                  <strong>Step 1:</strong> You will make your <strong>own individual prediction</strong>.
                </p>
                <p>
                  <strong>Step {discussionStepNumber}:</strong> Before your final estimate, you will review <strong>two other participants' responses</strong> to the same question. There is <strong>no live back-and-forth discussion</strong>. Use their estimates and reasoning as you would peer input in a group decision. When you are ready, click Continue to move on.
                </p>
              </>
            ) : usesShareableBaseRateGroup ? (
              <>
                <p>
                  <strong>Step 1:</strong> You will make your <strong>own individual prediction</strong>.
                </p>
                <p>
                  <strong>Step 2:</strong>{" "}
                  {usesTemplateFollowUp ? (
                    <>
                      You will then complete a <strong>structured follow-up questionnaire</strong>. Please provide <strong>a detailed explanation</strong> of your reasoning.
                    </>
                  ) : (
                    <>
                      You will then <strong>converse with an AI assistant</strong> about your prediction. Please provide <strong>a detailed explanation</strong> of your reasoning.
                    </>
                  )}
                </p>
                <p>
                  <strong>Step 3:</strong> You will confirm your <strong>{isBaseRateStaticSyntheticGroup ? "pre-review" : "pre-discussion"} prediction</strong>.
                </p>
                {!skipsPostConfirmWriteup && (
                  <p>
                    <strong>Step 4:</strong> After confirming, you will review{" "}
                    {usesTemplateFollowUp ? "your questionnaire answers" : "your prior dialog"} and write a <strong>detailed rationale of at least 50 words</strong>{isBaseRateStaticSyntheticGroup ? " to articulate your reasoning before you review the other participants' responses." : ". This writeup will appear in the group discussion so everyone can see your reasoning."}
                  </p>
                )}
                {hasPostConfirmPolishStep && !skipsPostConfirmWriteup && (
                  <p>
                    <strong>Step 5:</strong> You will review an <strong>AI-polished version</strong> of your writeup. You may edit it further or keep it as-is before continuing {isBaseRateStaticSyntheticGroup ? "to review the other participants' responses." : "before deciding the final text the group will see."}
                  </p>
                )}
                <p>
                  <strong>Step {discussionStepNumber}:</strong>{" "}
                  {isBaseRateStaticSyntheticGroup ? (
                    <>
                      Before your final estimate, you will review <strong>two other participants' responses</strong> to the same question. There is <strong>no live back-and-forth discussion</strong>. Use their estimates and reasoning as you would peer input in a group decision. When you are ready, click Continue to move on.
                    </>
                  ) : (
                    <>
                      You will enter the discussion page. Everyone first gets <strong>2 minutes to read the opening rationales and draft a first message</strong>. After that, the <strong>6-minute turn-taking group discussion</strong> begins.
                    </>
                  )}
                </p>
              </>
            ) : (
              <>
                <p>
                  <strong>Step 1:</strong> You will make your <strong>own individual prediction</strong> and then <strong>converse with an AI assistant</strong> about your prediction and explain your rationale. Please provide <strong>a detailed explanation</strong> of your reasoning as it will be used to assess the quality of your participation.
                </p>
                <p>
                  <strong>Step 2:</strong> After the conversation with the AI assistant, you will enter a <strong>6-minute group discussion</strong> with other participants.
                </p>
              </>
            )}
            {(isBaseRateGroup3 || !isBaseRateStaticSyntheticGroup) && (
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              {isBaseRateGroup3 && (
                <li><strong>2-minute prep window:</strong> Read the opening rationales and draft your first message. No one can send during this window.</li>
              )}
              {!isBaseRateStaticSyntheticGroup && (
                <>
                  <li><strong>Turn-taking:</strong> Players take turns speaking in a rotating order.</li>
                  <li><strong>Time limit:</strong> Each player has <strong>45 seconds</strong> to speak when it's their turn.</li>
                  <li><strong>Automatic turn advance:</strong> If you don't send a message within 45 seconds, your turn will automatically pass to the next player.</li>
                  <li><strong>You can always edit:</strong> You can type and edit your message at any time, even when it's not your turn. However, you can only <strong>send</strong> your message when it's your turn.</li>
                </>
              )}
            </ul>
            )}
            {postPeerAskAll && isPostPeerTiming ? (
              <p>
                <strong>Step {finalVoteStepNumber}:</strong> Finally, you will give your final answer by <strong>choosing one of three options</strong>: keep your own estimate, or adopt one of the two other participants' answers. If some of these answers turn out to be identical, you will see fewer options.
              </p>
            ) : (
              <p>
                <strong>Step {finalVoteStepNumber}:</strong> Finally, you will submit your{" "}
                <strong>
                  {isBaseRateStaticSyntheticGroup
                    ? "final individual estimate"
                    : "final individual prediction"}
                </strong>
                .
              </p>
            )}
          </div>
        </div>

        {/* 3. Reward setup */}
        <div className="bg-blue-50 rounded-md shadow-sm border border-blue-200 p-4">
          <h4 className="font-semibold text-blue-900 mb-3 text-lg">3. Reward Setup</h4>
          <div className="space-y-2 text-blue-800">
            <p>
              <strong>Base Payment:</strong> You will receive {compensation.basePaymentText}.
            </p>
            {taskConfig.quizEnabled && (
              <p>
                <strong>Quiz Bonus:</strong> Quiz correctness is linked to bonus. Each correct quiz question is worth <strong>$0.3</strong>.
              </p>
            )}
            {isBaseRateStaticSyntheticGroup ? (
              <>
                <p>
                  <strong>Accuracy Bonus:</strong> In addition to the base payment, you may receive <strong>{compensation.accuracyBonusText}</strong> if your <strong>final individual estimate</strong> is accurate.
                </p>
              </>
            ) : (
              <p>
                <strong>Accuracy Bonus:</strong> In addition to the base payment, you may receive <strong>{compensation.accuracyBonusText}</strong> if the <strong>majority vote of your group</strong> (not your individual final prediction) is accurate.
              </p>
            )}
            {!isBaseRateStaticSyntheticGroup && usesShareableBaseRateGroup && (
              <p>
                <strong>Base-rate majority rule:</strong> Because this task uses whole-number percentage answers, there is a group majority only if at least <strong>two of the three final answers exactly match</strong>. If all three final answers differ, there is <strong>no majority vote</strong> and therefore <strong>no group accuracy bonus</strong>.
              </p>
            )}
            {compensation.hasMostConvincingBonus && (
              <p>
                <strong>Most Convincing Reasoner Bonus:</strong> After the discussion, we will ask each player to choose the one player who provided the most convincing reasons. The one player who receives the highest number of votes will earn an additional <strong>{compensation.mostConvincingBonusText}</strong> bonus.
              </p>
            )}
          </div>
        </div>

        {/* Comprehension Questions */}
        <div className="bg-yellow-50 rounded-md shadow-sm border border-yellow-200 p-4">
          <h4 className="font-semibold text-yellow-900 mb-3 text-lg">Please answer the following questions to proceed:</h4>
          
          <div className="space-y-4">
            {/* Question 1 */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                1. {usesThreeStepList
                  ? "Before you give your final estimate, what will you do?"
                  : usesTemplateFollowUp
                  ? "What should you do in the structured follow-up questionnaire?"
                  : "What should you do when you converse with the AI assistant about your prediction?"}
              </p>
              <div className="space-y-1 ml-4">
                {usesThreeStepList ? (
                  <>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q1"
                        value="review"
                        checked={answers.q1 === "review"}
                        onChange={(e) => handleAnswerChange("q1", e.target.value)}
                        className="mr-2"
                      />
                      <span>You review two other participants' responses to the same question</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q1"
                        value="answer"
                        checked={answers.q1 === "answer"}
                        onChange={(e) => handleAnswerChange("q1", e.target.value)}
                        className="mr-2"
                      />
                      <span>You see the correct answer and how close your estimate was</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q1"
                        value="final"
                        checked={answers.q1 === "final"}
                        onChange={(e) => handleAnswerChange("q1", e.target.value)}
                        className="mr-2"
                      />
                      <span>You give your final estimate right away, without seeing anyone else's response</span>
                    </label>
                  </>
                ) : (
                  <>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q1"
                    value="brief"
                    checked={answers.q1 === "brief"}
                    onChange={(e) => handleAnswerChange("q1", e.target.value)}
                    className="mr-2"
                  />
                  <span>Provide a brief one-sentence explanation</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q1"
                    value="detailed"
                    checked={answers.q1 === "detailed"}
                    onChange={(e) => handleAnswerChange("q1", e.target.value)}
                    className="mr-2"
                  />
                  <span>Explain your rationale and provide a detailed explanation of your reasoning</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q1"
                    value="skip"
                    checked={answers.q1 === "skip"}
                    onChange={(e) => handleAnswerChange("q1", e.target.value)}
                    className="mr-2"
                  />
                  <span>
                    {isPostPeerTiming
                      ? usesTemplateFollowUp
                        ? "Skip the questionnaire and go directly to the final estimate"
                        : "Skip the conversation and go directly to the final estimate"
                      : isBaseRateStaticSyntheticGroup
                        ? usesTemplateFollowUp
                          ? "Skip the questionnaire and go directly to the group review"
                          : "Skip the conversation and go directly to the group review"
                        : usesTemplateFollowUp
                          ? "Skip the questionnaire and go directly to the group discussion"
                          : "Skip the conversation and go directly to the group discussion"}
                  </span>
                </label>
                  </>
                )}
              </div>
            </div>

            {/* Question 2 */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                2. The {compensation.accuracyBonusText} accuracy bonus is based on which outcome?
              </p>
              <div className="space-y-1 ml-4">
                {isBaseRateStaticSyntheticGroup ? (
                  <>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q2"
                        value="individual"
                        checked={answers.q2 === "individual"}
                        onChange={(e) => handleAnswerChange("q2", e.target.value)}
                        className="mr-2"
                      />
                      <span>Your final individual estimate</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q2"
                        value="average"
                        checked={answers.q2 === "average"}
                        onChange={(e) => handleAnswerChange("q2", e.target.value)}
                        className="mr-2"
                      />
                      <span>The average of the earlier participant estimates</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q2"
                        value="initial"
                        checked={answers.q2 === "initial"}
                        onChange={(e) => handleAnswerChange("q2", e.target.value)}
                        className="mr-2"
                      />
                      <span>Your initial prediction</span>
                    </label>
                  </>
                ) : (
                  <>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q2"
                        value="individual"
                        checked={answers.q2 === "individual"}
                        onChange={(e) => handleAnswerChange("q2", e.target.value)}
                        className="mr-2"
                      />
                      <span>Your individual final prediction</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q2"
                        value="group"
                        checked={answers.q2 === "group"}
                        onChange={(e) => handleAnswerChange("q2", e.target.value)}
                        className="mr-2"
                      />
                      <span>The majority vote of your group</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q2"
                        value="initial"
                        checked={answers.q2 === "initial"}
                        onChange={(e) => handleAnswerChange("q2", e.target.value)}
                        className="mr-2"
                      />
                      <span>Your initial prediction</span>
                    </label>
                  </>
                )}
              </div>
            </div>

            {/* Question 3 */}
            {!isBaseRateStaticSyntheticGroup && (
            <div>
              <p className="font-medium text-gray-900 mb-2">
                3. {`How can you earn the additional ${compensation.mostConvincingBonusText} bonus for being the most convincing reasoner?`}
              </p>
              <div className="space-y-1 ml-4">
                  <>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q3"
                        value="accurate"
                        checked={answers.q3 === "accurate"}
                        onChange={(e) => handleAnswerChange("q3", e.target.value)}
                        className="mr-2"
                      />
                      <span>By making the most accurate prediction</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q3"
                        value="convincing"
                        checked={answers.q3 === "convincing"}
                        onChange={(e) => handleAnswerChange("q3", e.target.value)}
                        className="mr-2"
                      />
                      <span>By receiving the most votes for providing the most convincing reasons</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q3"
                        value="participating"
                        checked={answers.q3 === "participating"}
                        onChange={(e) => handleAnswerChange("q3", e.target.value)}
                        className="mr-2"
                      />
                      <span>By participating in the discussion</span>
                    </label>
                  </>
              </div>
            </div>
            )}

            {/* Question 3 (syngroup) / Question 4 (other conditions) */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                {isBaseRateStaticSyntheticGroup ? "3." : "4."}{" "}
                {isBaseRateStaticSyntheticGroup
                  ? "Which statement about the earlier participant responses is correct?"
                  : usesShareableBaseRateGroup
                  ? "If all three final percentage answers are different, what happens to the group accuracy bonus?"
                  : "During the group discussion, when can you edit your message text?"}
              </p>
              <div className="space-y-1 ml-4">
                {isBaseRateStaticSyntheticGroup ? (
                  <>
	                    <label className="flex items-center">
	                      <input
	                        type="radio"
	                        name="q3"
	                        value="changing"
	                        checked={answers.q3 === "changing"}
	                        onChange={(e) => handleAnswerChange("q3", e.target.value)}
	                        className="mr-2"
	                      />
	                      <span>The earlier participant responses will change while you review them</span>
	                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q3"
                        value="not-simultaneous"
                        checked={answers.q3 === "not-simultaneous"}
                        onChange={(e) => handleAnswerChange("q3", e.target.value)}
                        className="mr-2"
                      />
                      <span>The task is not simultaneous; the responses come from earlier participants</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q3"
                        value="ignore"
                        checked={answers.q3 === "ignore"}
                        onChange={(e) => handleAnswerChange("q3", e.target.value)}
                        className="mr-2"
                      />
                      <span>You should ignore the earlier responses when making your final estimate</span>
                    </label>
                  </>
                ) : usesShareableBaseRateGroup ? (
                  <>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q4"
                        value="individual"
                        checked={answers.q4 === "individual"}
                        onChange={(e) => handleAnswerChange("q4", e.target.value)}
                        className="mr-2"
                      />
                      <span>The bonus is based on your own final answer instead</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q4"
                        value="no-majority"
                        checked={answers.q4 === "no-majority"}
                        onChange={(e) => handleAnswerChange("q4", e.target.value)}
                        className="mr-2"
                      />
                      <span>There is no group majority vote, so there is no group accuracy bonus</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q4"
                        value="average"
                        checked={answers.q4 === "average"}
                        onChange={(e) => handleAnswerChange("q4", e.target.value)}
                        className="mr-2"
                      />
                      <span>The three answers are averaged automatically for bonus purposes</span>
                    </label>
                  </>
                ) : (
                  <>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q4"
                        value="only-turn"
                        checked={answers.q4 === "only-turn"}
                        onChange={(e) => handleAnswerChange("q4", e.target.value)}
                        className="mr-2"
                      />
                      <span>Only when it's your turn</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q4"
                        value="edit"
                        checked={answers.q4 === "edit"}
                        onChange={(e) => handleAnswerChange("q4", e.target.value)}
                        className="mr-2"
                      />
                      <span>At any time, but you can only send when it's your turn</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="q4"
                        value="never"
                        checked={answers.q4 === "never"}
                        onChange={(e) => handleAnswerChange("q4", e.target.value)}
                        className="mr-2"
                      />
                      <span>You cannot edit your message</span>
                    </label>
                  </>
                )}
              </div>
            </div>

            
          </div>

          {showError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 text-sm">
                Please answer all questions correctly before proceeding.
              </p>
            </div>
          )}
        </div>
      </div>
      <Button handleClick={handleNext} autoFocus>
        <p>Next</p>
      </Button>
    </div>
  );
}
